import React from 'react';
import { hasLanguageChoice, offeredLanguages } from '../../i18n';
import { languageEndonym, type Language } from '../../lib/language';

interface LanguageSelectorProps {
  value: Language;
  onChange: (value: Language) => void;
  label: string;
  disabled?: boolean;
}

/**
 * A radio group of the offered languages, each named in itself ("English"), shared
 * by the signup form and Settings so the two stay identical. Renders nothing while
 * English is the only language offered.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ value, onChange, label, disabled = false }) => {
  if (!hasLanguageChoice()) return null;
  const languages = offeredLanguages();

  return (
    <fieldset className="w-full" disabled={disabled}>
      <legend className="block text-sm font-medium text-charcoal mb-1.5">{label}</legend>
      {/* One row: two or three languages side by side. */}
      <div className={`grid gap-2 ${languages.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {languages.map((lang) => {
          const selected = value === lang;
          return (
            <label
              key={lang}
              lang={lang}
              className={`
                flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer transition-all
                min-h-[44px]
                ${selected ? 'border-sage-500 bg-sage-50 shadow-glow' : 'border-faint hover:border-gray-300 bg-white'}
              `}
            >
              <input
                type="radio"
                name="language"
                value={lang}
                checked={selected}
                onChange={() => onChange(lang)}
                className="accent-sage-600"
              />
              <span className="text-sm font-medium text-gray-800">{languageEndonym(lang)}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};
